import React from 'react';
import { Modal, ModalBody, ModalHeader, Popover } from 'reactstrap';
import Sidebar from '../App/Sidebar/Sidebar';
import HotelSimpleItem from '../App/Hotels/HotelSimpleItem';
import HotelMapItem from '../App/Hotels/HotelMapItem';
import { apiSdk } from '@/httpClient';
import { AvailableHotelModel, HotelFilter, HotelSearchRequest } from '@/__generated';
import { GetHotelAvailableParams, IHotelQuery } from '@/pages/HotelSearch/type';
import Spin from '../Spin';
import { useTranslation } from 'react-i18next';
import { useCurrencyFormat } from '@/hooks/useCurrencyFormat';
import { generateHotelDetailUrl } from '@/urls';
import { BoardType } from '@/constants';

type Props = {
  onClose: () => void;
  sessionId: string;
  onSessionExpired: () => void;
  hotelQuery: IHotelQuery | null | undefined;
  onProviderChange: (isAllProvider: boolean) => void;
  params: GetHotelAvailableParams | undefined;
};
let map: google.maps.Map;

export default function HotelsMap({ onClose, sessionId, onSessionExpired, hotelQuery, onProviderChange, params }: Props) {
  const { t } = useTranslation();
  const { formatCurrency } = useCurrencyFormat();

  const [displayHotelInfo, setDisplayHotelInfo] = React.useState<AvailableHotelModel>();
  const [hotels, setHotels] = React.useState<AvailableHotelModel[]>([]);
  const [searchAvailableParams, setAvailableParams] = React.useState<GetHotelAvailableParams | undefined>(params);
  const [hotelFilter, setHotelFilter] = React.useState<HotelFilter>();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [hotelLoading, setHotelLoading] = React.useState<boolean>(true);

  const getHotels = (searchParams: Partial<GetHotelAvailableParams>) => {
    setHotelLoading(true);
    apiSdk.BookingService.getHotels({
      ...searchParams,
      sessionId: sessionId || '',
      pageSize: 1000,
    } as any)
      .then(res => {
        if (res.sessionExpired) {
          onSessionExpired();
          setAvailableParams(undefined);
        }
        setHotels(res.data || []);
      })
      .finally(() => {
        setHotelLoading(false);
      });
  };
  const getHotelFilterData = () => {
    setLoading(true);
    apiSdk.BookingService.getHotelFilters({ ...searchAvailableParams, sessionId: sessionId || '' } as any)
      .then(res => {
        setHotelFilter(res);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  React.useEffect(() => {
    if (sessionId) {
      getHotelFilterData();
    }
  }, [sessionId, searchAvailableParams]);
  React.useEffect(() => {
    if (hotelQuery && sessionId) {
      setAvailableParams(params);
    }
  }, [sessionId, hotelQuery, params]);
  React.useEffect(() => {
    if (sessionId) {
      getHotels(searchAvailableParams as any);
    }
  }, [sessionId]);

  React.useEffect(() => {
    if (hotels?.length) {
      initMap();
    }
  }, [hotels]);

  const onFilterChange = (params: Partial<GetHotelAvailableParams>, clearAllFilter?: boolean) => {
    let newSearchParams = { ...searchAvailableParams, ...params };
    if (clearAllFilter) {
      newSearchParams = { ...params, boardTypes: hotelQuery?.boardType ? [BoardType.__getValue(hotelQuery?.boardType)] : undefined };
    }
    setAvailableParams(newSearchParams as any);
    getHotels(newSearchParams);
  };

  const initMap = async () => {
    const { Map } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
    var bounds = new google.maps.LatLngBounds();
    hotels.forEach(hotel => {
      if (hotel.location && hotel.location.lat && hotel.location.lon) {
        bounds.extend({
          lat: hotel.location.lat,
          lng: hotel.location.lon,
        });
      }
    });

    map = new Map(document.getElementById('hotels-map') as HTMLElement, {
      zoom: 16,
      disableDefaultUI: true,
      zoomControl: true,
      gestureHandling: 'greedy',
      mapId: '4504f8b37365c3d0',
      draggableCursor: 'default',
    });

    map.fitBounds(bounds);
    window.hotelsMap = map;
    hotels.forEach(hotel => {
      if (hotel.location && hotel.location.lat && hotel.location.lon) {
        const priceTag = document.createElement('div');
        priceTag.className = 'map-hotel-tag';
        priceTag.textContent = formatCurrency(hotel.cheapestPrice);
        priceTag.id = `hotel-id-${hotel.hotelCode}`;
        priceTag.setAttribute('data-hotel-id', hotel.hotelCode + '');
        new AdvancedMarkerElement({
          map,
          position: { lat: hotel.location?.lat, lng: hotel.location.lon },
          content: priceTag,
        });
        priceTag.onclick = () => {
          window.open(generateHotelDetailUrl(hotel.hotelName, hotel.hotelCode), '_blank');
        };
        priceTag.onmouseover = () => {
          setDisplayHotelInfo(hotel);
        };
        priceTag.onmouseleave = () => {
          setDisplayHotelInfo(undefined);
        };
      }
    });
  };

  return (
    <Modal isOpen={true} unmountOnClose={true} fullscreen>
      <ModalHeader toggle={onClose} className="text-blue-1">
        {t('hotelListPage_HotelsMap')}
      </ModalHeader>
      <ModalBody>
        <div className="row">
          <div className="col-2 omega-scrollbar " style={{ maxHeight: 'calc(100vh - 85px)' }}>
            <Spin loading={loading}>
              <aside className="sidebar y-gap-40 xl:d-none">
                {hotelFilter ? (
                  <Sidebar
                    hotelQuery={hotelQuery}
                    sessionId={sessionId}
                    params={searchAvailableParams}
                    hotelFilter={hotelFilter}
                    onFilterChange={onFilterChange}
                    onProviderChange={onProviderChange}
                  />
                ) : null}
              </aside>
            </Spin>
          </div>
          <div className="col-4 omega-scrollbar" style={{ maxHeight: 'calc(100vh - 85px)', height: 'calc(100vh - 85px)' }}>
            <div className="row y-gap-10 h-100">
              <Spin loading={hotelLoading}>
                {hotels.map(hotel => {
                  return <HotelSimpleItem hotel={hotel} key={hotel.hotelCode} />;
                })}
              </Spin>
            </div>
          </div>
          <div className="col-6">
            <div id="hotels-map" style={{ width: '100%', height: '100%' }} />
          </div>
        </div>
        {displayHotelInfo ? (
          <Popover className="hotel-map-popover" isOpen={true} target={`hotel-id-${displayHotelInfo.hotelCode}`}>
            <HotelMapItem hotel={displayHotelInfo} />
          </Popover>
        ) : null}
      </ModalBody>
    </Modal>
  );
}
